/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { QUESTION } from './terminology';
import { UserMin } from './users';

export enum UserRoles {
  Owner = 'owner',
  Manager = 'manager',
  User = 'user',
  Contributor = 'contributor',
  Verifier = 'verifier',
  ContributorVerifier = 'contributor_verifier',
  Guest = 'guest',
  Viewer = 'viewer'
}

const stakeholderType: { [key: string]: string } = {
  auditor: 'Auditor',
  business_sector: 'Business Sector',
  commercial_investor: 'Commercial Investor',
  fund_manager: 'Fund Manager',
  international_government: 'International Government',
  national_government: 'National Government',
  local_government: 'Local Government',
  non_profit_sector: 'Non Profit Sector',
  philanthropic_donor: 'Philanthropic Donor',
  public_member: 'Public Member',
};

const partnerTypes: { [key: string]: string } = {
  assurer: 'Assurer',
  beneficiary: 'Beneficiary',
  delivery: 'Delivery',
  financial: 'Financial',
  other: 'Other',
}

export interface RoleInformation {
  disabled?: boolean;
  code: UserRoles;
  label: string;
  icon: string;
  description: string;
  requiresVerificationAccess?: boolean;
}

export const USER_ROLE_LABEL_MAP = {
  [UserRoles.Owner]: 'Owner',
  [UserRoles.Manager]: 'Administrator',
  [UserRoles.Contributor]: 'Contributor',
  [UserRoles.Verifier]: 'Verifier',
  [UserRoles.ContributorVerifier]: 'Contributor and Verifier',
  [UserRoles.User]: 'User',
  [UserRoles.Viewer]: 'Viewer',
  /* @deprecated */
  [UserRoles.Guest]: 'Guest',
};

export const companyTrackerUserRoles: RoleInformation[] = [
  {
    disabled: true,
    code: UserRoles.Owner,
    label: 'Owner',
    icon: 'fa-user',
    description: ''
  },
  {
    code: UserRoles.Manager,
    label: 'Administrator',
    icon: 'fa-user',
    description: 'Administrators are able to modify all details of your organisation, including billing, materiality, custom metrics, manage users, and creating new surveys. This permission gives full access to the platform and so it is recommended to limit the number of users with this permission.'
  },
  {
    code: UserRoles.Contributor,
    label: 'Contributor',
    icon: 'fa-user',
    description: `Contributors are able to contribute answers to all ${QUESTION.PLURAL} for all surveys in your organisation. Contributors can also verify ${QUESTION.PLURAL}, but only if they have been explicitly delegated as a verifier to a ${QUESTION.SINGULAR} or survey.`
  },
  {
    code: UserRoles.Verifier,
    label: 'Verifier',
    icon: 'fa-user',
    description: `Verifiers are able to verify all ${QUESTION.PLURAL} that have been assigned as in-scope for surveys in your organisation. Verifiers can also contribute to ${QUESTION.PLURAL}, but only if they have been explicitly delegated as a contributor to a ${QUESTION.SINGULAR} or survey.`,
    requiresVerificationAccess: true,
  },
  {
    code: UserRoles.ContributorVerifier,
    label: 'Contributor and Verifier',
    icon: 'fa-user',
    description: `Accounts with this permission are able to contribute answers to all ${QUESTION.PLURAL} and verify all ${QUESTION.PLURAL} that have been assigned as in-scope for surveys in your organisation, without needing to be explicitly delegated to the ${QUESTION.SINGULAR} by an admin.`,
    requiresVerificationAccess: true,
  },
  {
    code: UserRoles.Viewer,
    label: 'Viewer',
    icon: 'fa-user',
    description: `Viewers are able to view all ${QUESTION.PLURAL}, but can only contribute or verify if they are explicitly delegated to a ${QUESTION.SINGULAR} or survey.`,
  },
  {
    code: UserRoles.User,
    label: 'Restricted user',
    icon: 'fa-user',
    description: `This is the default role. Users are unable to view, contribute or verify ${QUESTION.PLURAL} unless they are explicitly delegated to a ${QUESTION.SINGULAR} or survey.`,
  },
  {
    disabled: true,
    code: UserRoles.Guest,
    label: 'Guest',
    icon: 'fa-user',
    description: 'This is the most basic access user. They will have limited access to the company profile but are unable to see private data or navigate reporting levels. This is the recommended role for users outside your organization.'
  }
]

export const portfolioTrackerUserRoles: RoleInformation[] = [
  {
    code: UserRoles.Manager,
    label: 'Administrator',
    icon: 'fa-user',
    description: 'This user is able to manage this account, including managing custom metrics, users, and portfolios. This role gives full access and is recommended only for administrators.'
  },
  {
    code: UserRoles.Viewer,
    label: 'Viewer',
    icon: 'fa-user',
    description: 'Viewers are able to view and download data.',
  },
]

export const getStakeHolderType = (type: string) => stakeholderType[type] || type

export const getPartnerType = (type: string) => partnerTypes[type] || type

export const getUserFullName = (user?: UserMin) => {
  if (!user) {
    return '';
  }

  return `${user.firstName ?? ''} ${user.surname ?? ''}`.trim()
}

export const getRoleName = (role: UserRoles, maxLength?: number) => {
  const userRole = companyTrackerUserRoles.find(r => r.code === role);
  const name = userRole?.label ?? role;
  if (maxLength && name.length > maxLength) {
    return `${name.substring(0, maxLength)}...`;
  }
  return name;
}

export const filterValidRoles = (role: UserRoles) => Object.values(UserRoles).includes(role);

export const getChecked = (role: UserRoles, selectedRoles: UserRoles[]) => {
  switch (role) {
    case UserRoles.Contributor:
      return selectedRoles.includes(UserRoles.Contributor) && !selectedRoles.includes(UserRoles.Verifier);
    case UserRoles.Verifier:
      return selectedRoles.includes(UserRoles.Verifier) && !selectedRoles.includes(UserRoles.Contributor);
    case UserRoles.ContributorVerifier:
      return selectedRoles.includes(UserRoles.Contributor) && selectedRoles.includes(UserRoles.Verifier);
    default:
      return selectedRoles.includes(role);
  }
}

export const handleRoleRadioChange = (role: UserRoles, action: 'add' | 'remove', prevRoles: UserRoles[]) => {
  if (action === 'remove') {
    const newRoles = prevRoles.filter((prevRole) => prevRole !== role);
    if (newRoles.length === 0) {
      return [UserRoles.Viewer];
    }
    return newRoles;
  }

  const basicRoles = [UserRoles.User, UserRoles.Viewer, UserRoles.Guest];
  if (basicRoles.includes(role)) {
    return [role];
  }

  if (role === UserRoles.Owner) {
    return [...prevRoles.filter(r => !basicRoles.includes(r)), UserRoles.Owner, UserRoles.Manager];
  }

  if (role === UserRoles.Manager) {
    return [...prevRoles.filter(r => !basicRoles.includes(r)), UserRoles.Manager];
  }

  let newRoles = prevRoles.filter(r => r === UserRoles.Owner || r === UserRoles.Manager);
  if (role === UserRoles.ContributorVerifier) {
    newRoles = [...newRoles, UserRoles.Verifier, UserRoles.Contributor];
  } else {
    newRoles.push(role);
  }

  return newRoles;
}
